import { useMemo } from "react"
import { usePackageAssignmentQuery, usePackageQuery } from "@pathwright/graphql-client/src/generated"
import { PackageDataSource } from "./types"
import { getPackageParams, mapUrlParamsToVariables } from "./utils"

type UsePackageOptions = {
  searchParams?: URLSearchParams
  dataSource?: PackageDataSource
}

const hasResolvedPackageParams = (urlParams: URLSearchParams) => {
  for (const value of urlParams.values()) {
    if (value?.trim()) return true
  }

  return false
}

// Resolve via the `package` query (URL params and/or server store rules) or via
// `packageAssignment` (post-signup assignment on the current school).
// - dataSource "url": always use `package` (empty vars still hit resolve-package for STORE)
// - dataSource "assignment": always use `packageAssignment`
// - unset: URL params → `package`; otherwise `packageAssignment`
const getShouldUseAssignmentQuery = (
  urlParams: URLSearchParams,
  dataSource?: PackageDataSource
) => {
  return (
    dataSource === "assignment" ||
    (!dataSource && !hasResolvedPackageParams(urlParams))
  )
}

export const usePackage = (options: UsePackageOptions = {}) => {
  const { searchParams, dataSource } = options

  const urlParams = useMemo(
    () => getPackageParams(searchParams),
    [searchParams]
  )

  const shouldUseAssignmentQuery = useMemo(
    () => getShouldUseAssignmentQuery(urlParams, dataSource),
    [urlParams, dataSource]
  )

  const variables = useMemo(
    () => mapUrlParamsToVariables(urlParams),
    [urlParams]
  )

  const packageQuery = usePackageQuery({
    variables,
    skip: shouldUseAssignmentQuery
  })
  const packageAssignmentQuery = usePackageAssignmentQuery({
    skip: !shouldUseAssignmentQuery
  })
  const packageData = shouldUseAssignmentQuery
    ? packageAssignmentQuery.data?.packageAssignment?.package || null
    : packageQuery.data?.package || null
  const loading = shouldUseAssignmentQuery
    ? packageAssignmentQuery.loading
    : packageQuery.loading
  const error = shouldUseAssignmentQuery
    ? packageAssignmentQuery.error
    : packageQuery.error

  return {
    packageData,
    loading,
    error,
    source: shouldUseAssignmentQuery ? "assignment" : "params",
    assignment: packageAssignmentQuery.data?.packageAssignment || null
  }
}
